// Dropbox Service for importing files from Dropbox
import { Dropbox } from 'dropbox';

export interface DropboxFile {
  id: string;
  name: string;
  path_lower: string;
  size: number;
  content_hash?: string;
  client_modified?: string;
  server_modified?: string;
}

export interface DropboxFolder {
  id: string;
  name: string;
  path_lower: string;
}

export interface DropboxMetadata {
  files: DropboxFile[];
  folders: DropboxFolder[];
  cursor?: string;
  has_more: boolean;
}

/** User-facing English copy when Dropbox access token is invalid or expired. */
export const DROPBOX_SESSION_EXPIRED_USER_MESSAGE =
  'Your Dropbox session has expired. Please sign in again.';

function rawSummaryFromDropboxError(error: any): string {
  return (
    error?.error?.error_summary ??
    error?.error?.error?.['.tag'] ??
    error?.message ??
    ''
  );
}

/**
 * Maps Dropbox SDK / API errors to safe English UI text.
 * Callers can compare `message === DROPBOX_SESSION_EXPIRED_USER_MESSAGE` to clear session.
 */
export function normalizeDropboxApiError(error: any, fallback: string): string {
  const summary = rawSummaryFromDropboxError(error);
  const lower = summary.toLowerCase();
  if (lower.includes('expired_access_token') || lower.includes('invalid_access_token')) {
    return DROPBOX_SESSION_EXPIRED_USER_MESSAGE;
  }
  if (lower.includes('scope') || lower.includes('files.metadata.read')) {
    return 'Missing Dropbox permission. Disconnect and connect again to grant access.';
  }
  if (summary) return summary;
  return error?.message || fallback;
}

class DropboxService {
  private dbx: Dropbox | null = null;
  private accessToken: string | null = null;

  /**
   * Initialize Dropbox client with access token
   */
  initialize(accessToken: string) {
    this.accessToken = accessToken;
    this.dbx = new Dropbox({
      accessToken: accessToken,
    });
  }

  /**
   * Check if Dropbox is initialized
   */
  isInitialized(): boolean {
    return this.dbx !== null && this.accessToken !== null;
  }

  /**
   * Get OAuth authorization URL
   * Note: This should be handled via your backend for security
   */
  static getAuthUrl(redirectUri: string, clientId: string): string {
    // Generate OAuth authorization URL manually
    const authBaseUrl = 'https://www.dropbox.com/oauth2/authorize';
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      token_access_type: 'offline', // Request refresh token
      // Note: Scopes should be configured in Dropbox App Console under "Permissions" tab
      // Required scopes: files.metadata.read, files.content.read
    });
    
    return `${authBaseUrl}?${params.toString()}`;
  }

  /**
   * List files and folders in a directory
   */
  async listFolder(path: string = ''): Promise<DropboxMetadata> {
    if (!this.dbx) {
      throw new Error('Dropbox not initialized. Please authenticate first.');
    }

    try {
      // Dropbox API v2 requires empty string for root folder
      // Ensure path is properly formatted (no leading/trailing slashes except for root)
      let normalizedPath = path.trim();
      if (normalizedPath === '' || normalizedPath === '/') {
        normalizedPath = '';
      } else {
        // Ensure path starts with / and doesn't end with /
        normalizedPath = normalizedPath.startsWith('/') 
          ? normalizedPath 
          : `/${normalizedPath}`;
        normalizedPath = normalizedPath.endsWith('/') && normalizedPath.length > 1
          ? normalizedPath.slice(0, -1)
          : normalizedPath;
      }
      
      const response = await this.dbx.filesListFolder({ 
        path: normalizedPath 
      });
      
      const files: DropboxFile[] = [];
      const folders: DropboxFolder[] = [];

      for (const entry of response.result.entries) {
        if (entry['.tag'] === 'file') {
          // Only include audio files
          const file = entry as any;
          const fileName = file.name.toLowerCase();
          const isAudioFile = 
            fileName.endsWith('.mp3') ||
            fileName.endsWith('.wav') ||
            fileName.endsWith('.flac') ||
            fileName.endsWith('.aiff') ||
            fileName.endsWith('.aif') ||
            fileName.endsWith('.ogg') ||
            fileName.endsWith('.m4a') ||
            fileName.endsWith('.wma');

          if (isAudioFile) {
            files.push({
              id: file.id,
              name: file.name,
              path_lower: file.path_lower,
              size: file.size,
              content_hash: file.content_hash,
              client_modified: file.client_modified,
              server_modified: file.server_modified,
            });
          }
        } else if (entry['.tag'] === 'folder') {
          const folder = entry as any;
          folders.push({
            id: folder.id || folder.path_lower,
            name: folder.name,
            path_lower: folder.path_lower,
          });
        }
      }

      return {
        files,
        folders,
        cursor: response.result.cursor,
        has_more: response.result.has_more || false,
      };
    } catch (error: any) {
      console.error('❌ Dropbox list folder error:', error);
      
      // Log more detailed error information
      if (error.status) {
        console.error('Error status:', error.status);
      }
      if (error.error) {
        console.error('Error details:', error.error);
      }
      if (error.response) {
        console.error('Error response:', error.response);
      }
      
      const errorMessage = normalizeDropboxApiError(error, 'Failed to list Dropbox folder');
      throw new Error(errorMessage);
    }
  }

  /**
   * Download a file from Dropbox and convert to Blob
   */
  async downloadFile(path: string): Promise<Blob> {
    if (!this.dbx) {
      throw new Error('Dropbox not initialized. Please authenticate first.');
    }

    try {
      const response = await this.dbx.filesDownload({ path });
      
      // The Dropbox SDK returns fileBinary as an ArrayBuffer or Blob
      // Check for fileBlob first (browser environment)
      const result = response.result as any;
      
      if (result.fileBlob) {
        return result.fileBlob as Blob;
      }
      
      // Fallback: create Blob from ArrayBuffer
      if (result.fileBinary) {
        const arrayBuffer = result.fileBinary;
        // Determine MIME type from file extension in path
        const extension = path.split('.').pop()?.toLowerCase() || '';
        const mimeType = this.getMimeType(extension);
        return new Blob([arrayBuffer], { type: mimeType });
      }
      
      // Last resort: try to extract from response body
      if (result instanceof Blob) {
        return result;
      }
      
      throw new Error('Failed to get file data from Dropbox response');
    } catch (error: any) {
      console.error('❌ Dropbox download error:', error);
      throw new Error(normalizeDropboxApiError(error, 'Failed to download file from Dropbox'));
    }
  }

  /**
   * Download a file and convert to File object
   */
  async downloadFileAsFile(path: string, fileName: string): Promise<File> {
    const blob = await this.downloadFile(path);
    
    // Get file extension from original name
    const extension = fileName.split('.').pop() || '';
    const mimeType = this.getMimeType(extension);
    
    return new File([blob], fileName, { type: mimeType });
  }

  /**
   * Get MIME type from file extension
   */
  private getMimeType(extension: string): string {
    const mimeTypes: Record<string, string> = {
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      flac: 'audio/flac',
      aiff: 'audio/aiff',
      aif: 'audio/aiff',
      ogg: 'audio/ogg',
      m4a: 'audio/mp4',
      wma: 'audio/x-ms-wma',
    };

    return mimeTypes[extension.toLowerCase()] || 'application/octet-stream';
  }

  /**
   * Continue listing folder with cursor (for pagination)
   */
  async listFolderContinue(cursor: string): Promise<DropboxMetadata> {
    if (!this.dbx) {
      throw new Error('Dropbox not initialized. Please authenticate first.');
    }

    try {
      const response = await this.dbx.filesListFolderContinue({ cursor });
      
      const files: DropboxFile[] = [];
      const folders: DropboxFolder[] = [];

      for (const entry of response.result.entries) {
        if (entry['.tag'] === 'file') {
          const file = entry as any;
          const fileName = file.name.toLowerCase();
          const isAudioFile = 
            fileName.endsWith('.mp3') ||
            fileName.endsWith('.wav') ||
            fileName.endsWith('.flac') ||
            fileName.endsWith('.aiff') ||
            fileName.endsWith('.aif') ||
            fileName.endsWith('.ogg') ||
            fileName.endsWith('.m4a') ||
            fileName.endsWith('.wma');

          if (isAudioFile) {
            files.push({
              id: file.id,
              name: file.name,
              path_lower: file.path_lower,
              size: file.size,
              content_hash: file.content_hash,
              client_modified: file.client_modified,
              server_modified: file.server_modified,
            });
          }
        } else if (entry['.tag'] === 'folder') {
          const folder = entry as any;
          folders.push({
            id: folder.id || folder.path_lower,
            name: folder.name,
            path_lower: folder.path_lower,
          });
        }
      }

      return {
        files,
        folders,
        cursor: response.result.cursor,
        has_more: response.result.has_more || false,
      };
    } catch (error: any) {
      console.error('❌ Dropbox list folder continue error:', error);
      throw new Error(
        normalizeDropboxApiError(error, 'Failed to continue listing Dropbox folder')
      );
    }
  }

  /**
   * Get file metadata
   */
  async getFileMetadata(path: string): Promise<DropboxFile | null> {
    if (!this.dbx) {
      throw new Error('Dropbox not initialized. Please authenticate first.');
    }

    try {
      const response = await this.dbx.filesGetMetadata({ path });
      
      if (response.result['.tag'] !== 'file') {
        return null;
      }

      const file = response.result as any;
      return {
        id: file.id,
        name: file.name,
        path_lower: file.path_lower,
        size: file.size,
        content_hash: file.content_hash,
        client_modified: file.client_modified,
        server_modified: file.server_modified,
      };
    } catch (error: any) {
      console.error('❌ Dropbox get metadata error:', error);
      return null;
    }
  }

  /**
   * Clear access token (logout)
   */
  clear() {
    this.accessToken = null;
    this.dbx = null;
  }
}

// Export singleton instance
export const dropboxService = new DropboxService();
